import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ACTIONS } from '../../constants/actionTables';
import TableComponent from '../../components/Table';
import { requestSearch } from '../../helpers/search';
import { fetchSuppliesData } from '../../helpers/fetchData';
import PageLoader from '../../components/PageLoader';

import { Typography } from '@mui/material';

import styles from './style.module.css';
import { useSupplies } from '../../hooks/useSupplies';
import ModalAddSupplie from '../../components/ModalAddSupplie';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';

function Supplies() {
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const { user } = useAuth();
  const dispatch = useDispatch();
  const { supplies, isLoading } = useSupplies();

  // Load supplies data on-demand for this page
  useEffect(() => {
    if (supplies.length === 0) {
      fetchSuppliesData(dispatch);
    }
  }, [dispatch, supplies.length]);
  const { t } = useTranslation();

  useEffect(() => {
    if (supplies) {
      const preparedData = supplies.map((supplie) => ({
        id: supplie.id,
        number: supplie.number,
        name: supplie.name,
        description: supplie.description,
        manufacture: supplie.manufacture,
        safetySheet: supplie.original_filename,
        link: supplie.filename,
        flagForDelete: supplie.is_able_to_remove,
      }));
      setData(preparedData);
      setFilteredData(preparedData);
    }
  }, [supplies]);

  const titles = [
    t('generic.number'),
    t('generic.name'),
    t('generic.description'),
    t('generic.manufacture'),
    t('generic.safetySheet'),
  ];

  if (user && user.role === 'admin') {
    titles.push(t('generic.action'));
  }

  const handleSearch = (searchValue) => {
    // Reset sorting when searching
    if (searchValue.trim() !== '') {
      setSortConfig({ key: null, direction: 'asc' });
    }
    requestSearch(searchValue, data || [], setFilteredData);
  };

  const getSuppliesCount = () => {
    return filteredData?.length || 0;
  };

  const handleSort = (columnKey) => {
    // Implement three-state sorting: asc → desc → reset (no sorting)
    let newSortConfig = { key: null, direction: 'asc' };

    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'asc') {
        // Switch from asc to desc
        newSortConfig = { key: columnKey, direction: 'desc' };
      } else if (sortConfig.direction === 'desc') {
        // Reset sorting (third click)
        newSortConfig = { key: null, direction: 'asc' };
      }
    } else {
      // New column clicked - start with asc
      newSortConfig = { key: columnKey, direction: 'asc' };
    }

    setSortConfig(newSortConfig);

    // If resetting, restore original order
    if (newSortConfig.key === null) {
      setFilteredData([...data]);
      return;
    }

    // Apply sorting
    const dataToSort = [...filteredData];
    dataToSort.sort((a, b) => {
      const aValue = a[columnKey];
      const bValue = b[columnKey];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string') {
        return newSortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return newSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    setFilteredData(dataToSort);
  };

  return (
    <PageLoader
      loading={isLoading}
      headerTitle={t('generic.supplies')}
      headerSubtitle={t('generic.suppliesSubtitle')}
    >
      <section className={styles.container}>
        <div className={styles.header}>
          <div className={styles.caption}>
            <Typography variant="h5" fontWeight="bold" className={styles.headerTitle}>
              {t('generic.supplies')}
            </Typography>
            <Typography className={styles.headerSubtitle}>{t('generic.suppliesSubtitle')}</Typography>
          </div>

          {user && user.role === 'admin' && <ModalAddSupplie type={'add'} />}
        </div>

        {filteredData && (
          <TableComponent
            titles={titles}
            data={filteredData}
            action={user && user.role === 'admin' && ACTIONS.SUPPLIES}
            search={true}
            showTableHeader={true}
            tableTitle={t('generic.allSupplies')}
            tableCount={`${getSuppliesCount()} ${t('generic.suppliesText')}`}
            searchPlaceholder={t('generic.search')}
            onSearch={handleSearch}
            onSort={handleSort}
            sortConfig={sortConfig}
            sortableColumns={['number', 'name', 'description', 'manufacture', 'safetySheet']}
            columnKeys={['number', 'name', 'description', 'manufacture', 'safetySheet', 'action']}
          />
        )}
      </section>
    </PageLoader>
  );
}

export default Supplies;
